import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight } from "lucide-react";
import { API, scrollToTop, setCanonical } from "../lib/config";
import { Nav, Footer, HeroBanner, CookieConsent } from "../components/layout";
import { ChatBot } from "../components/chat";
import { AffiliateBanner, AdSensePlaceholder, MoneyStrip } from "../components/ads";

export default function StatesDirectory() {
  const [states, setStates] = useState([]);

  useEffect(() => {
    setCanonical("/states");
    document.title = "All 50 States | Auto Insurance Guide | QuotesByCity";
    axios.get(`${API}/states`).then(r => setStates(r.data.states || [])).catch(() => {});
  }, []);

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <HeroBanner title="Auto Insurance by State" subtitle="Minimum requirements, average rates, and city guides for all 50 states" badge="All States" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* MAIN CONTENT */}
          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="states-grid">
              {states.map(s => (
                <Link key={s.code} to={`/states/${s.slug}`} onClick={scrollToTop} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-sky-300 hover:shadow-sm transition-all group" data-testid={`state-card-${s.code}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm group-hover:text-sky-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{s.city_count} cities | Min: {s.min_auto}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-sky-700">${s.avg_auto_premium?.toLocaleString()}</p>
                      <p className="text-[10px] text-slate-400">avg/year</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* MOBILE ADS - after states grid */}
            <div className="lg:hidden">
              <MoneyStrip />
            </div>

            {/* CAN'T FIND YOUR CITY BANNER */}
            <div className="bg-slate-100 border border-slate-200 rounded-2xl p-6 sm:p-8 text-center mt-8" data-testid="cant-find-city-banner">
              <MapPin size={24} className="text-sky-700 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Can't Find Your City?</h3>
              <p className="text-sm text-slate-500 mb-4">Don't worry — our auto insurance guide covers every city in the US. Type any city for an instant guide.</p>
              <Link to="/find-your-city" onClick={scrollToTop} className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-xl px-6 py-3 text-sm font-medium hover:bg-sky-800 transition-all">
                Auto Insurance Guide for All Cities <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* SIDEBAR - Desktop */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6 hidden lg:block" data-testid="sidebar">
            <div className="lg:sticky lg:top-20 space-y-6">
              <AffiliateBanner />
              <AdSensePlaceholder size="large" />
            </div>
          </aside>
        </div>
      </div>
      <Footer />
      <ChatBot />
      <CookieConsent />
    </div>
  );
}
